import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isEqual } from "lodash";
import actions from "../actions";
import { useHistory, useParams, useLocation } from "react-router-dom";
import {
    findInfoPathFromBreadCrump,
    findPrevPathFromBreadCrumpData,
    popBreadCrumpOnPageClose,
    showAlert as showAlertUtil
} from "../../../config/utils";
import qs from "query-string";
import { setFormDirty } from "../../common/actions";

const useLeaseForm = () => {
    const { id: lease_id, section } = useParams();
    const dispatch = useDispatch();
    const [initialData, setInitialData] = useState({
        name: "",
        description: "",
        start_date: "",
        end_date: "",
        color_code: "",
        region_id: "",
        site_id: "",
        building_id: "",
        dynamic_fields: {},
        sf_years: {}
    });

    const [data, setData] = useState({
        name: "",
        description: "",
        start_date: "",
        end_date: "",
        color_code: "",
        region_id: "",
        site_id: "",
        building_id: "",
        dynamic_fields: {},
        sf_years: {}
    });

    const [fieldSearch, setFieldSearch] = useState("");
    const [errorParams, setErrorParams] = useState({});
    const [showErrorBorder, setShowErrorBorder] = useState(false);
    const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const history = useHistory();
    const {
        getLeaseDynamicFieldsResponse,
        getLeaseBuildingByIdResponse,
        addLeaseBuildingsResponse,
        updateLeaseBuildingsResponse,
        getLeaseBuildingByIdLoading
    } = useSelector(state => state.leaseBuildingReducer);
    const { isFormDirty } = useSelector(state => state.commonReducer);
    const query = qs.parse(useLocation()?.search);
    const submitDataRef = useRef(null);
    const updateDataRef = useRef(null);

    useEffect(() => {
        if (section === "add") {
            const updatedData = { ...data };
            if (query?.region_id) {
                updatedData.region_id = query?.region_id;
            }
            if (query?.site_id) {
                updatedData.site_id = query?.site_id;
            }
            if (query?.bid) {
                updatedData.building_id = query?.bid;
            }
            setData(updatedData);
            setInitialData(updatedData);
        }
    }, []);

    useEffect(() => {
        dispatch(setFormDirty(!isEqual(data, initialData)));
    }, [data]);

    useEffect(() => {
        if (getLeaseBuildingByIdResponse && lease_id) {
            const rawDynamicFields = getLeaseBuildingByIdResponse?.dynamic_fields || {};

            const simplifiedDynamicFields = Object.entries(rawDynamicFields).reduce((acc, [key, valueObj]) => {
                acc[key] = valueObj?.value ?? "";
                return acc;
            }, {});
            setData(prev => ({
                ...prev,
                name: getLeaseBuildingByIdResponse?.name || "",
                description: getLeaseBuildingByIdResponse?.description || "",
                start_date: getLeaseBuildingByIdResponse?.start_date || "",
                end_date: getLeaseBuildingByIdResponse?.end_date || "",
                color_code: getLeaseBuildingByIdResponse?.color_code || "",
                region_id: getLeaseBuildingByIdResponse?.region?.id || "",
                site_id: getLeaseBuildingByIdResponse?.site?.id || "",
                building_id: getLeaseBuildingByIdResponse?.building?.id || "",
                sf_years: getLeaseBuildingByIdResponse?.sf_years || {},
                dynamic_fields: { ...prev.dynamic_fields, ...simplifiedDynamicFields }
            }));
            setInitialData(prev => ({
                ...prev,
                name: getLeaseBuildingByIdResponse?.name || "",
                description: getLeaseBuildingByIdResponse?.description || "",
                start_date: getLeaseBuildingByIdResponse?.start_date || "",
                end_date: getLeaseBuildingByIdResponse?.end_date || "",
                color_code: getLeaseBuildingByIdResponse?.color_code || "",
                region_id: getLeaseBuildingByIdResponse?.region?.id || "",
                site_id: getLeaseBuildingByIdResponse?.site?.id || "",
                building_id: getLeaseBuildingByIdResponse?.building?.id || "",
                sf_years: getLeaseBuildingByIdResponse?.sf_years || {},
                dynamic_fields: { ...prev.dynamic_fields, ...simplifiedDynamicFields }
            }));
        }
    }, [getLeaseBuildingByIdResponse]);

    const showAlert = () => showAlertUtil(alertMessage);

    useEffect(() => {
        if (alertMessage) {
            showAlert();
            setAlertMessage("");
        }
    }, [alertMessage]);

    useEffect(() => {
        if (submitDataRef?.current)
            if (addLeaseBuildingsResponse) {
                submitDataRef.current = false;
                const { success, message = "", detail = null, error = null } = addLeaseBuildingsResponse;
                if (success) {
                    setAlertMessage(message || "Lease created successfully");
                    setTimeout(() => {
                        cancelForm();
                    }, 1000);
                } else {
                    setAlertMessage(typeof detail === "string" ? detail : message ? message : error);
                }
            }
    }, [addLeaseBuildingsResponse]);

    useEffect(() => {
        if (updateDataRef?.current)
            if (updateLeaseBuildingsResponse) {
                updateDataRef.current = false;
                const { success, message = "", detail = null, error = null } = updateLeaseBuildingsResponse;
                if (success) {
                    setAlertMessage(message || "Lease updated successfully");
                    setTimeout(() => {
                        cancelForm();
                    }, 1000);
                } else {
                    setAlertMessage(typeof detail === "string" ? detail : message ? message : error);
                }
            }
    }, [updateLeaseBuildingsResponse]);

    const getChangedDynamicFields = (current = {}, initial = {}) => {
        const changed = {};
        for (const key in current) {
            // if (current[key] !== initial[key]) {
            changed[key] = current[key];
            // }
        }
        return changed;
    };

    const handleSubmit = () => {
        if (validateForm()) {
            const changedDynamicFields = getChangedDynamicFields(data?.dynamic_fields, initialData?.dynamic_fields);
            const dataValue = {
                ...data,
                dynamic_fields: Object.keys(changedDynamicFields).length > 0 ? [changedDynamicFields] : {}
            };

            const payload = {
                ...dataValue,
                client_id: query?.cid,
                project_id: query?.pid
            };

            if (lease_id) {
                dispatch(actions?.updateBuildingLease(lease_id, payload));
                updateDataRef.current = true;
            } else {
                dispatch(actions?.addBuildingLease(payload));
                submitDataRef.current = true;
            }
        }
    };

    const validateForm = onChangeData => {
        const dataToBeValidated = onChangeData ?? data;
        const { name = "", start_date = "", end_date = "", dynamic_fields = {}, sf_years = {} } = dataToBeValidated;

        const errorParams = {};
        let showErrorBorder = false;

        const requiredFields = [
            { key: "name", label: "Name", value: name },
            { key: "start_date", label: "Start Date", value: start_date },
            { key: "end_date", label: "End Date", value: end_date },
            { key: "region_id", label: "Region", value: dataToBeValidated?.region_id },
            { key: "site_id", label: "Site", value: dataToBeValidated?.site_id },
            { key: "building_id", label: "Building", value: dataToBeValidated?.building_id }
        ];

        requiredFields.forEach(({ key, label, value }) => {
            if ((typeof value === "string" && value.trim() === "") || !value) {
                errorParams[key] = `${label} is required`;
                showErrorBorder = true;
            }
        });

        if (start_date && end_date) {
            const start = new Date(start_date);
            const end = new Date(end_date);
            if (end <= start) {
                errorParams.end_date = "End Date should be greater than Start Date";
                showErrorBorder = true;
            }
        }

        Object.keys(sf_years).forEach(yearKey => {
            if (!sf_years?.[yearKey]?.value || sf_years[yearKey]?.value?.trim() === "") {
                errorParams[yearKey] = `${yearKey} SF is required`;
                showErrorBorder = true;
            }
        });

        const allFields = Object.values(getLeaseDynamicFieldsResponse?.lease_custom_fields || {}).flat();

        allFields.forEach(({ field_name, label, rules }) => {
            const value = dynamic_fields[field_name];
            const isMandatory = rules?.mandatory;

            if (isMandatory && (value === null || value === undefined || (typeof value === "string" && value?.trim() === ""))) {
                errorParams[field_name] = `${label || field_name} is required`;
                showErrorBorder = true;
            }
        });

        setShowErrorBorder(showErrorBorder);
        setErrorParams(errorParams);

        return !showErrorBorder;
    };

    const onCancel = () => {
        if (isFormDirty) setShowCancelConfirmModal(true);
        else cancelForm();
    };

    const cancelForm = async () => {
        dispatch(setFormDirty(false));
        popBreadCrumpOnPageClose();
        await dispatch(actions?.resetReducer());
        history.push(findPrevPathFromBreadCrumpData());
    };

    const handleChange = (e, band, isStatic, isDynamicField) => {
        const { name, value } = e;
        let changedObject = { ...data };
        if (isDynamicField && !getLeaseBuildingByIdLoading) {
            changedObject = { ...data, dynamic_fields: e?.dynamic_fields };
        } else {
            if (isStatic) {
                if (name.startsWith("years.")) {
                    const yearKey = name.split(".")[1];
                    const incoming = value && typeof value === "object" ? value : { label: "", value: value ?? "" };
                    const updatedYears = {
                        ...(changedObject.sf_years || {}),
                        [yearKey]: incoming
                    };
                    changedObject = { ...changedObject, sf_years: updatedYears };
                } else {
                    changedObject = { ...changedObject, [name]: value };
                }
            } else {
                changedObject = { ...changedObject, dynamic_fields: { ...changedObject?.dynamic_fields, [name]: value } };
            }
        }
        if (name === "region_id") {
            changedObject = { ...changedObject, site_id: "", building_id: "" };
        } else if (name === "site_id") {
            changedObject = { ...changedObject, building_id: "" };
        }
        setData({ ...changedObject });
    };

    return {
        data,
        handleSubmit,
        onCancel,
        handleChange,
        showErrorBorder,
        errorParams,
        showCancelConfirmModal,
        setShowCancelConfirmModal,
        cancelForm,
        initialData,
        fieldSearch,
        setFieldSearch
    };
};

export default useLeaseForm;
